import { curSym } from "@/lib/company";
import { TaxOfficePicker } from "@/components/TaxOfficePicker";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useCallback, useEffect, useState } from "react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PhoneInput, isValidPhone } from "@/components/PhoneInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EntityAvatar, AvatarUpload } from "@/components/EntityAvatar";
import { toast } from "sonner";
import { fmtTL } from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import EntityDetailDialog from "@/components/EntityDetailDialog";
import { Users2, Plus, Pencil, Trash2, Loader2, Search, Phone, Percent, X, Receipt, Wallet, MapPin, FileSpreadsheet, Tag, Download, Eye } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { useTablePrefs } from "@/lib/useTablePrefs";
import { ColumnToggle } from "@/components/ColumnToggle";
import { TR_CITIES } from "@/lib/tr-cities";
import TaxPortalLookup from "@/components/TaxPortalLookup";
import { PriceInput } from "@/components/PriceInput";
import { TR_REGIONS } from "@/lib/regions";

const PROVINCES = Object.keys(TR_CITIES).sort((a, b) => a.localeCompare(b, "tr"));

const EMPTY = {
  name: "", type: "individual", avatar_url: "", phone: "", email: "", tax_office: "", tax_no: "",
  province: "", district: "", address: "", note: "", default_discount_pct: 0, min_order_amount: 0, can_wholesale: false, birthday: "", category_discounts: [],
  e_invoice_portal: "", e_document_type: "", tax_verified_at: "",
};

const CUST_COLS = [
  { key: "name", label: "Ad / Firma", locked: true },
  { key: "contact", label: "İletişim (telefon)" },
  { key: "tax", label: "VKN / TCKN" },
  { key: "location", label: "İl / İlçe" },
  { key: "stats", label: "Ciro & Alışveriş" },
  { key: "tags", label: "İndirim / Toptan Rozetleri" },
];

export default function Customers() {
  const { can, user, feature } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = can("customer.manage");
  const { visible: colVis, density: rowDensity, toggle: toggleCol, setDensity: setColDensity, reset: resetCols } = useTablePrefs("customers", CUST_COLS);
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [fProvince, setFProvince] = useState("");
  const [fRegion, setFRegion] = useState("");
  const [fType, setFType] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stmt, setStmt] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailDocs, setDetailDocs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const customersPaged = usePaged(list);
  useEffect(() => { setSelected(new Set()); }, [list]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (fProvince) p.set("province", fProvince);
    else if (fRegion) p.set("region", fRegion);
    if (fType) p.set("type", fType);
    const qs = p.toString();
    api.get(`/customers${qs ? `?${qs}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  }, [q, fProvince, fRegion, fType]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const catNames = Array.from(new Set(cats.map((c) => c.name))).sort((a, b) => a.localeCompare(b, "tr"));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", type: c.type || "individual", avatar_url: c.avatar_url || "", phone: c.phone || "", email: c.email || "",
      tax_office: c.tax_office || "", tax_no: c.tax_no || "",
      province: c.province || "", district: c.district || "", address: c.address || "", note: c.note || "",
      default_discount_pct: c.default_discount_pct || 0,
      min_order_amount: c.min_order_amount || 0,
      can_wholesale: !!c.can_wholesale,
      birthday: c.birthday || "",
      category_discounts: (c.category_discounts || []).map((r) => ({ category: r.category, discount_pct: r.discount_pct })),
      e_invoice_portal: c.e_invoice_portal || "", e_document_type: c.e_document_type || "", tax_verified_at: c.tax_verified_at || "",
    });
    setOpen(true);
  };

  const openStatement = async (c) => {
    setStmt({ customer: c, loading: true }); setStmtLoading(true);
    try { const { data } = await api.get(`/customers/${c.id}/statement`); setStmt(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setStmt(null); }
    finally { setStmtLoading(false); }
  };

  const openDetail = async (c) => {
    setDetail(c); setDetailDocs(c.documents || []);
    try { const { data } = await api.get(`/customers/${c.id}`); setDetail(data); setDetailDocs(data.documents || []); } catch (e) { /* liste verisiyle devam */ }
  };
  const addCustomerDoc = async (body) => {
    const { data } = await api.post(`/customers/${detail.id}/documents`, body);
    setDetailDocs((d) => [...d, data]); toast.success("Evrak eklendi");
  };
  const delCustomerDoc = async (id) => {
    await api.delete(`/customers/${detail.id}/documents/${id}`);
    setDetailDocs((d) => d.filter((x) => x.id !== id)); toast.success("Evrak silindi");
  };

  const addRule = () => setForm((f) => ({ ...f, category_discounts: [...f.category_discounts, { category: catNames[0] || "", discount_pct: 0 }] }));
  const setRule = (i, k, v) => setForm((f) => ({ ...f, category_discounts: f.category_discounts.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const rmRule = (i) => setForm((f) => ({ ...f, category_discounts: f.category_discounts.filter((_, idx) => idx !== i) }));

  const requestSave = () => {
    if (!form.name.trim()) return toast.error("Müşteri adı zorunlu");
    if (form.phone && !isValidPhone(form.phone)) return toast.error("Telefon numarası geçersiz — ülke koduna göre hane sayısını kontrol edin");
    setConfirmOpen(true);
  };
  const doSave = async () => {
    if (!form.name.trim()) return toast.error("Ad / Firma adı zorunlu");
    if (form.phone && !isValidPhone(form.phone)) return toast.error("Telefon numarası geçersiz");
    if (!isAdmin) {
      if (!form.phone) return toast.error("Telefon zorunlu");
      if (!(form.email || "").trim()) return toast.error("E-posta zorunlu");
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("E-posta geçersiz");
      if (!(form.tax_no || "").trim()) return toast.error(form.type === "corporate" ? "VKN zorunlu" : "TCKN zorunlu");
      if (feature("field.tax_office") && form.type === "corporate" && !(form.tax_office || "").trim()) return toast.error("Vergi dairesi zorunlu");
      if (!form.province) return toast.error("İl zorunlu");
      if (!form.district) return toast.error("İlçe zorunlu");
      if (!(form.address || "").trim()) return toast.error("Açık adres zorunlu");
      if (!(form.tax_verified_at || "").trim()) return toast.error("VKN/TCKN doğrulanmadan kayıt yapılamaz — 'Doğrula & Getir' ile doğrulayın");
    }
    setSaving(true);
    const body = {
      ...form,
      default_discount_pct: Number(form.default_discount_pct) || 0,
      min_order_amount: Number(form.min_order_amount) || 0,
      category_discounts: form.category_discounts
        .filter((r) => r.category)
        .map((r) => ({ category: r.category, discount_pct: Number(r.discount_pct) || 0 })),
    };
    try {
      const res = editing ? await api.put(`/customers/${editing.id}`, body) : await api.post("/customers", body);
      if (res.data?.pending_approval) toast.info(res.data.message || "Değişiklik Yönetici onayına gönderildi");
      else toast.success(editing ? "Müşteri güncellendi" : "Müşteri eklendi");
      setConfirmOpen(false); setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`"${c.name}" müşterisi silinsin mi?`)) return;
    try { const res = await api.delete(`/customers/${c.id}`); if (res.data?.pending_approval) toast.info(res.data.message || "Silme Yönetici onayına gönderildi"); else toast.success("Müşteri silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/customers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "musteriler.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Dışa aktarım başarısız"); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selCustomers = list.filter((c) => selected.has(c.id));
  const bulkTag = async () => {
    const tag = window.prompt("Seçili müşterilere eklenecek etiket:");
    if (!tag || !tag.trim()) return;
    try { const { data } = await api.post("/customers/bulk-tag", { ids: [...selected], tag: tag.trim() }); toast.success(`${data.modified} müşteriye "${data.tag}" etiketi eklendi`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Etiketleme başarısız"); }
  };
  const exportSelectedCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Ad", "Tür", "Telefon", "E-posta", "VKN/TCKN", "İl", "İlçe", "Etiketler"];
    const rows = selCustomers.map((c) => [c.name, c.type === "corporate" ? "Kurumsal" : "Bireysel", c.phone, c.email, c.tax_no, c.province, c.district, (c.tags || []).join("; ")].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...rows].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "secili-musteriler.csv"; a.click(); window.URL.revokeObjectURL(url);
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data;
      toast.success(`İçe aktarıldı: ${d.created} yeni, ${d.updated} güncellendi, ${d.skipped} atlandı`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İçe aktarım başarısız"); }
    finally { e.target.value = ""; }
  };

  const req = {
    name: !!form.name.trim(),
    phone: !!form.phone && isValidPhone(form.phone),
    email: !!(form.email || "").trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.email || "").trim()),
    tax_no: !!(form.tax_no || "").trim(),
    tax_office: !feature("field.tax_office") || form.type !== "corporate" || !!(form.tax_office || "").trim(),
    province: !!form.province,
    district: !!form.district,
    address: !!(form.address || "").trim(),
    verified: !!(form.tax_verified_at || "").trim(),
  };
  const missLabels = {
    name: "Ad / Firma Adı", phone: "Telefon", email: "E-posta",
    tax_no: form.type === "corporate" ? "VKN" : "TCKN", tax_office: "Vergi Dairesi",
    province: "İl", district: "İlçe", address: "Açık Adres",
    verified: "VKN/TCKN Doğrulaması (Doğrula & Getir)",
  };
  const missing = Object.keys(req).filter((k) => !req[k]).map((k) => missLabels[k]);

  return (
    <div className="space-y-4 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users2 className="h-5 w-5 text-brand-green" />
          <div><p className="overline">CRM</p><h1 className="font-heading text-2xl font-bold text-slate-900">Müşteriler</h1></div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="İsim / telefon / vergi no ara..." className="w-56 pl-9" />
          </div>
          {canManage && (<>
            <input id="cust-import-file" type="file" accept=".xlsx" className="hidden" data-testid="customer-import-input" onChange={importExcel} />
            <Button data-testid="customer-import-btn" onClick={() => document.getElementById("cust-import-file").click()} variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/5">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel'den Al
            </Button>
          </>)}
          <ColumnToggle testid="customers-table-tools" cols={CUST_COLS} visible={colVis} onToggle={toggleCol} density={rowDensity} onDensity={setColDensity} onReset={resetCols} />
          <Button data-testid="customer-export-btn" onClick={exportExcel} variant="outline" className="border-slate-300">
            <FileSpreadsheet className="mr-1.5 h-4 w-4 text-emerald-600" /> Excel'e At
          </Button>
          {canManage && (
            <Button data-testid="customer-new-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Müşteri
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-md bg-white p-2 ring-1 ring-inset ring-slate-200" data-testid="customer-filters">
        <span className="text-xs font-semibold text-slate-500">Filtrele:</span>
        <select data-testid="customer-filter-province" value={fProvince} onChange={(e) => { setFProvince(e.target.value); if (e.target.value) setFRegion(""); }}
          className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
          <option value="">Tüm İller</option>
          {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="customer-filter-region" value={fRegion} disabled={!!fProvince} onChange={(e) => setFRegion(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:opacity-50">
          <option value="">Tüm Bölgeler</option>
          {TR_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="customer-filter-type" value={fType} onChange={(e) => setFType(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
          <option value="">Tüm Türler</option>
          <option value="individual">Bireysel</option>
          <option value="corporate">Kurumsal</option>
        </select>
        {(fProvince || fRegion || fType) && (
          <button data-testid="customer-filter-clear" onClick={() => { setFProvince(""); setFRegion(""); setFType(""); }}
            className="text-xs font-semibold text-red-500 hover:underline">Temizle</button>
        )}
        <span className="ml-auto text-xs text-slate-400">{list.length} müşteri</span>
      </div>

      {canManage && selected.size > 0 && (
        <div data-testid="customers-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-md bg-brand-green/5 p-2 ring-1 ring-inset ring-brand-green/30">
          <span className="text-sm font-semibold text-brand-green">{selected.size} müşteri seçili</span>
          <Button data-testid="customers-bulk-tag" onClick={bulkTag} size="sm" variant="outline" className="border-brand-green/40 text-brand-green hover:bg-brand-green/10"><Tag className="mr-1.5 h-4 w-4" /> Toplu Etiket</Button>
          <Button data-testid="customers-bulk-csv" onClick={exportSelectedCsv} size="sm" variant="outline" className="border-slate-300"><Download className="mr-1.5 h-4 w-4" /> Seçilenleri CSV</Button>
          <button data-testid="customers-bulk-clear" onClick={() => setSelected(new Set())} className="ml-auto text-xs font-semibold text-slate-500 hover:underline">Seçimi temizle</button>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${rowDensity === "compact" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {list.length === 0 && <div className="col-span-full"><EmptyState icon={Users2} testid="customers-empty" title="Kayıtlı müşteri yok" hint="'Yeni Müşteri' ile ilk müşterinizi ekleyin — VKN/TCKN doğrulaması ile eksiksiz kayıt." /></div>}
        {customersPaged.slice.map((c) => (
          <div key={c.id} data-testid={`customer-card-${c.id}`} className={`rounded-md bg-white ${rowDensity === "compact" ? "p-3" : "p-4"} ring-1 ring-inset ring-slate-200`}>
            <div className="mb-2 flex items-start justify-between">
              <div className="flex items-center gap-2">
                {canManage && <input type="checkbox" data-testid={`customer-select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} className="h-4 w-4 accent-brand-green" />}
                <EntityAvatar src={c.avatar_url} name={c.name} size={40} />
                <div>
                  <p className="font-heading font-bold text-slate-800">{c.name}</p>
                  <p className="text-[11px] text-slate-400">{c.type === "corporate" ? "Kurumsal" : "Bireysel"}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`customer-detail-${c.id}`} onClick={() => openDetail(c)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100" title="Detay & Evraklar"><Eye className="h-4 w-4" /></button>
                {canManage && <button data-testid={`customer-edit-${c.id}`} onClick={() => openEdit(c)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100" title="Düzenle"><Pencil className="h-4 w-4" /></button>}
                {canManage && <button data-testid={`customer-del-${c.id}`} onClick={() => del(c)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50" title="Sil"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-500">
              {colVis.contact && c.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {c.phone}</p>}
              {colVis.tax && c.tax_no && <p>VKN/TCKN: <span className="tabular">{c.tax_no}</span></p>}
              {colVis.location && (c.province || c.district) && <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {[c.district, c.province].filter(Boolean).join(" / ")}</p>}
            </div>

            {(c.tags || []).length > 0 && (
              <div data-testid={`customer-tags-${c.id}`} className="mt-2 flex flex-wrap gap-1">
                {c.tags.map((t) => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"><Tag className="h-3 w-3" /> {t}</span>)}
              </div>
            )}

            {colVis.stats && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div className="rounded-sm bg-slate-50 p-2">
                <p className="overline flex items-center gap-1"><Wallet className="h-3 w-3" /> Toplam Ciro</p>
                <p className="tabular text-sm font-bold text-brand-green">{fmtTL(c.total_revenue || 0)}</p>
              </div>
              <div className="rounded-sm bg-slate-50 p-2">
                <p className="overline flex items-center gap-1"><Receipt className="h-3 w-3" /> Alışveriş</p>
                <p className="tabular text-sm font-bold text-slate-700">{c.sales_count || 0} <span className="text-[11px] font-normal text-slate-400">adet</span></p>
              </div>
            </div>
            )}

            {colVis.tags && (
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[11px] font-semibold text-brand-orange">
                <Percent className="h-3 w-3" /> Genel %{c.default_discount_pct || 0}
              </span>
              {c.min_order_amount > 0 && (
                <span data-testid={`customer-minorder-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                  <Wallet className="h-3 w-3" /> Toptan min {fmtTL(c.min_order_amount)}
                </span>
              )}
              {c.can_wholesale && (
                <span data-testid={`customer-wholesale-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[11px] font-semibold text-brand-orange">
                  <Tag className="h-3 w-3" /> Toptan uygun
                </span>
              )}
              {(c.category_discounts || []).map((r, i) => (
                <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{r.category}: %{r.discount_pct}</span>
              ))}
            </div>
            )}

            <Button data-testid={`customer-statement-${c.id}`} onClick={() => openStatement(c)} variant="outline" size="sm" className="mt-3 w-full h-8 text-xs">
              <Receipt className="mr-1.5 h-3.5 w-3.5" /> Ekstre / Geçmiş
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <Pagination page={customersPaged.page} pageSize={customersPaged.pageSize} total={customersPaged.total}
          onPage={customersPaged.setPage} onPageSize={customersPaged.setPageSize} testid="customers-pagination" />
      </div>

      {/* Create / edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[86vh] max-w-xl overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</DialogTitle>
            <DialogDescription><span className="font-semibold text-red-500">*</span> işaretli alanlar ve <b>VKN/TCKN doğrulaması</b> zorunludur. Sekmeler arasında geçiş yapın.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pb-1"><AvatarUpload value={form.avatar_url} name={form.name} onChange={(url) => setForm({ ...form, avatar_url: url })} size={64} /></div>
          <Tabs defaultValue="identity" className="w-full">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-lg bg-slate-100 p-1" data-testid="customer-tabs">
              <TabsTrigger value="identity" data-testid="cust-tab-identity" className="flex-1">Kimlik</TabsTrigger>
              <TabsTrigger value="address" data-testid="cust-tab-address" className="flex-1">Adres & Vergi</TabsTrigger>
              <TabsTrigger value="commercial" data-testid="cust-tab-commercial" className="flex-1">Toptan & İskonto</TabsTrigger>
            </TabsList>
            <div className="mt-3">
              <TabsContent value="identity" className="mt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Ad / Firma Adı *</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
                  <div>
                    <Label>Tür</Label>
                    <div className="mt-1 inline-flex rounded-md bg-slate-100 p-1">
                      {[["individual", "Bireysel"], ["corporate", "Kurumsal"]].map(([id, label]) => (
                        <button key={id} data-testid={`customer-type-${id}`} onClick={() => setForm({ ...form, type: id })}
                          className={`rounded-sm px-3 py-1 text-xs font-semibold ${form.type === id ? "bg-brand-green text-white" : "text-slate-500"}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div><Label>Doğum Günü</Label><DatePickerTR testid="customer-birthday" value={form.birthday} onChange={(v) => setForm({ ...form, birthday: v })} /></div>
                  <div><Label>Telefon *</Label><div className="mt-1"><PhoneInput testid="customer-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></div></div>
                  <div><Label>E-posta *</Label><Input data-testid="customer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                </div>
              </TabsContent>
              <TabsContent value="address" className="mt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{form.type === "corporate" ? "VKN *" : "TCKN *"}</Label><Input data-testid="customer-taxno" value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value, tax_verified_at: "" })} className="mt-1" /></div>
                  {form.type === "corporate" && feature("field.tax_office") && (
                    <div><Label>Vergi Dairesi *</Label><TaxOfficePicker testid="customer-taxoffice" value={form.tax_office} onChange={(v) => setForm({ ...form, tax_office: v })} /></div>
                  )}
                  <div>
                    <Label>İl *</Label>
                    <select data-testid="customer-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value, district: "" })}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                      <option value="">İl seçin</option>
                      {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>İlçe *</Label>
                    <select data-testid="customer-district" value={form.district} disabled={!form.province} onChange={(e) => setForm({ ...form, district: e.target.value })}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="">İlçe seçin</option>
                      {(TR_CITIES[form.province] || []).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><Label>Açık Adres *</Label><Input data-testid="customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
                </div>
                <div className="mt-3">
                  <TaxPortalLookup taxNo={form.tax_no} type={form.type} portal={form.e_invoice_portal} docType={form.e_document_type}
                    verifiedAt={form.tax_verified_at} name={form.name}
                    onSelect={({ e_document_type, e_invoice_portal }) => setForm((f) => ({ ...f, e_document_type, e_invoice_portal }))}
                    onEmail={(email) => setForm((f) => ({ ...f, email }))}
                    onVerified={(ts) => setForm((f) => ({ ...f, tax_verified_at: ts }))} testid="customer-tax-portal" />
                </div>
              </TabsContent>
              <TabsContent value="commercial" className="mt-0 space-y-3">
                <div className="rounded-md bg-emerald-50/60 p-3 ring-1 ring-inset ring-emerald-100">
                  <label data-testid="customer-can-wholesale" className="flex cursor-pointer items-center gap-2 pb-2">
                    <input type="checkbox" checked={!!form.can_wholesale} onChange={(e) => setForm({ ...form, can_wholesale: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800"><Tag className="h-4 w-4" /> Toptan (Fırsatlı) Alışverişe Uygun</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-emerald-600" />
                    <Label className="mb-0">Toptan Min. Alışveriş Limiti ({curSym()})</Label>
                    <PriceInput data-testid="customer-min-order" type="number" min="0" value={form.min_order_amount}
                      onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} wrapperClassName="ml-auto w-36" className="h-8 w-full tabular" />
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500"><b>Toptan uygun</b> müşteride kasada SMS/amir onayı istenir. Min. limit: sepet bu tutarı geçmeden toptan satış tamamlanamaz. 0 = limit yok.</p>
                </div>

                <div className="rounded-md bg-slate-50 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Percent className="h-4 w-4 text-brand-orange" />
                    <Label className="mb-0">Genel İskonto (%)</Label>
                    <Input data-testid="customer-default-disc" type="number" value={form.default_discount_pct}
                      onChange={(e) => setForm({ ...form, default_discount_pct: e.target.value })} className="ml-auto h-8 w-24 tabular" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="overline">Kategori Bazlı İskonto</p>
                    <button data-testid="customer-add-rule" onClick={addRule} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline"><Plus className="h-3.5 w-3.5" /> Ekle</button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {form.category_discounts.length === 0 && <p className="text-[11px] text-slate-400">Kategori bazlı özel oran yok. Genel iskonto uygulanır.</p>}
                    {form.category_discounts.map((r, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`customer-rule-${i}`}>
                        <select value={r.category} onChange={(e) => setRule(i, "category", e.target.value)}
                          className="h-9 flex-1 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                          {catNames.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <div className="relative w-24">
                          <Input type="number" value={r.discount_pct} onChange={(e) => setRule(i, "discount_pct", e.target.value)} className="h-9 tabular pr-6" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                        </div>
                        <button onClick={() => rmRule(i)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          {canManage && missing.length > 0 && (
            <p data-testid="customer-required-hint" className={`mt-3 rounded-md px-3 py-2 text-[11px] font-medium ring-1 ring-inset ${isAdmin ? "bg-sky-50 text-sky-700 ring-sky-200" : "bg-amber-50 text-amber-700 ring-amber-200"}`}>
              {isAdmin ? "ℹ Yönetici: eksik bilgiyle de kaydedebilirsiniz — eksik: " : "⚠ Kayıt için zorunlu (eksik): "}{missing.join(", ")}
            </p>
          )}
          <Button data-testid="customer-save" onClick={requestSave} disabled={saving || !canManage || (!isAdmin && missing.length > 0)} className="mt-3 w-full bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Kaydet" : "Ekle"}
          </Button>
        </DialogContent>
      </Dialog>

      {detail && (
        <EntityDetailDialog open onClose={() => setDetail(null)} testid="customer-detail-dialog"
          title={detail.name} avatarSrc={detail.avatar_url} avatarName={detail.name}
          subtitle={detail.type === "corporate" ? "Kurumsal Müşteri" : "Bireysel Müşteri"}
          sections={[
            { heading: "Genel", rows: [
              { label: "Telefon", value: detail.phone },
              { label: "E-posta", value: detail.email },
              { label: "Doğum Günü", value: detail.birthday },
            ]},
            { heading: "Vergi & e-Belge", rows: [
              { label: "Vergi Dairesi", value: detail.tax_office },
              { label: "VKN / TCKN", value: detail.tax_no, testid: "customer-detail-taxno" },
              { label: "e-Fatura Portalı", value: detail.e_invoice_portal },
              { label: "Belge Tipi", value: detail.e_document_type },
              { label: "Doğrulama", value: detail.tax_verified_at ? String(detail.tax_verified_at).slice(0, 10) : "Doğrulanmadı", accent: detail.tax_verified_at ? "text-emerald-600" : "text-rose-500" },
            ]},
            { heading: "Adres", rows: [
              { label: "İl / İlçe", value: [detail.district, detail.province].filter(Boolean).join(" / ") },
              { label: "Açık Adres", value: detail.address, full: true },
            ]},
            { heading: "Ticari & İstatistik", rows: [
              { label: "Genel İndirim", value: `%${detail.default_discount_pct || 0}` },
              { label: "Toptan Min. Tutar", value: detail.min_order_amount ? fmtTL(detail.min_order_amount) : "—" },
              { label: "Toptan Uygun", value: detail.can_wholesale ? "Evet" : "Hayır" },
              { label: "Toplam Ciro", value: fmtTL(detail.total_revenue || 0), accent: "text-brand-green" },
              { label: "Alışveriş Adedi", value: `${detail.sales_count || 0} adet` },
              { label: "Not", value: detail.note, full: true },
            ]},
          ]}
          docs={detailDocs}
          docConfig={{ canWrite: canManage, onAdd: addCustomerDoc, onDelete: delCustomerDoc, testid: "customer-docs", zipPath: `/customers/${detail.id}/documents/zip` }}
          footer={<button data-testid="customer-detail-statement" onClick={() => { const c = detail; setDetail(null); openStatement(c); }} className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-green px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-green-600"><Receipt className="h-4 w-4" /> Cari Ekstre / Hesap Özeti</button>} />
      )}
      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} loading={saving} onConfirm={doSave}
        title={editing ? "Müşteriyi güncelle" : "Müşteri ekle"}
        description={`"${form.name}" müşteri bilgilerini kaydetmek istediğinize emin misiniz?`}
        confirmText={editing ? "Evet, kaydet" : "Evet, ekle"} />

      {/* Statement */}
      <Dialog open={!!stmt} onOpenChange={(o) => !o && setStmt(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="statement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Receipt className="h-5 w-5 text-brand-green" /> {stmt?.customer?.name} · Ekstre</DialogTitle>
            <DialogDescription>Müşterinin geçmiş alışverişleri ve toplam cirosu.</DialogDescription>
          </DialogHeader>
          {stmtLoading ? (
            <div className="h-40 animate-pulse rounded-md bg-slate-100" />
          ) : stmt?.totals ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-emerald-50 p-3 text-center"><p className="overline">Toplam Ciro</p><p className="tabular text-lg font-bold text-emerald-700">{fmtTL(stmt.totals.total_revenue)}</p></div>
                <div className="rounded-md bg-slate-50 p-3 text-center"><p className="overline">Alışveriş</p><p className="tabular text-lg font-bold text-slate-700">{stmt.totals.sales_count}</p></div>
                <div className="rounded-md bg-orange-50 p-3 text-center"><p className="overline">Ort. Sepet</p><p className="tabular text-lg font-bold text-orange-700">{fmtTL(stmt.totals.avg_basket)}</p></div>
              </div>
              <p className="text-xs text-slate-500">Toplam verilen iskonto: <b className="tabular text-red-600">{fmtTL(stmt.totals.total_discount)}</b></p>
              <div className="overflow-hidden rounded-md ring-1 ring-inset ring-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                    <tr><th className="px-3 py-2">Fiş</th><th className="px-3 py-2">Tarih</th><th className="px-3 py-2 text-center">Kalem</th><th className="px-3 py-2 text-right">Tutar</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {stmt.sales.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Bu müşteriye ait satış yok.</td></tr>}
                    {stmt.sales.map((s) => (
                      <tr key={s.sale_no} data-testid={`stmt-row-${s.sale_no}`}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{s.sale_no}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleDateString("tr-TR") : "—"}</td>
                        <td className="px-3 py-2 text-center tabular text-slate-600">{s.items}</td>
                        <td className="px-3 py-2 text-right tabular font-semibold text-slate-800">{fmtTL(s.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
